import { motion } from "framer-motion";
import { CheckCircle2, Pencil, Eye, Zap, Shield, Layers } from "lucide-react";
import { useState, useEffect } from "react";
import { ContactModal } from "@/components/ContactModal";
import { useScrollAnimations } from "@/components/GSAPScrollAnimations";

const HowToDesignLogo = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const scrollContainer = useScrollAnimations();

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "How to Design a Logo Step by Step",
      "description": "Learn the complete logo design process from research and concept to final delivery. Step-by-step guide for creating professional logos.",
      "author": {
        "@type": "Organization",
        "name": "Koech Creatives",
        "url": "https://koech-creatives.onrender.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Koech Creatives",
        "logo": {
          "@type": "ImageObject",
          "url": "https://koech-creatives.onrender.com/og-image.png"
        }
      },
      "datePublished": "2026-07-13",
      "dateModified": "2026-07-13",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://koech-creatives.onrender.com/how-to-design-a-logo"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <main ref={scrollContainer} className="min-h-screen bg-gradient-to-b from-background to-accent-navy/30">
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            How to Design a Logo Step by Step
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            
            className="text-xl text-muted-foreground mb-8"
          >
            Learn the complete logo design process from concept to final delivery. This comprehensive guide walks you through each step of creating a professional logo that builds brand recognition.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Get Professional Logo Design
          </motion.button>
        </div>
      </section>

      {/* Step 1: Research */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Step 1: Research and Discovery
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground mb-6"
          >
            Before designing, understand your brand, audience, and competition. This foundation ensures your logo communicates the right message and stands out in your market.
          </motion.p>
          <div className="space-y-4">
            {[
              { task: "Analyze your brand values, mission, and personality", icon: Eye },
              { task: "Research target audience demographics and preferences", icon: Eye },
              { task: "Study competitor logos to identify opportunities", icon: Eye },
              { task: "Define your brand's unique selling proposition", icon: Eye },
            ].map((item, index) => (
              <motion.div
                key={item.task}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-4 rounded-lg border border-gray-800 flex gap-4"
              >
                <item.icon className="w-8 h-8 text-coral flex-shrink-0" />
                <p className="text-muted-foreground">{item.task}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Step 2: Concept Development */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Step 2: Concept Development
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground mb-6"
          >
            Generate multiple logo concepts based on your research. Explore different directions and styles before narrowing down to the strongest ideas.
          </motion.p>
          <div className="space-y-4">
            {[
              { task: "Brainstorm keywords related to your brand", icon: Pencil },
              { task: "Sketch 10-20 rough concepts by hand", icon: Pencil },
              { task: "Explore different logo types (wordmark, symbol, combination)", icon: Pencil },
              { task: "Apply design principles to each concept", icon: Pencil },
            ].map((item, index) => (
              <motion.div
                key={item.task}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-4 rounded-lg border border-gray-800 flex gap-4"
              >
                <item.icon className="w-8 h-8 text-coral flex-shrink-0" />
                <p className="text-muted-foreground">{item.task}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Step 3: Digital Design */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Step 3: Digital Design
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground mb-6"
          >
            Convert your best sketches into digital format using vector design software. This step refines the design and prepares it for professional use.
          </motion.p>
          <div className="space-y-4">
            {[
              { task: "Select 2-3 strongest concepts to digitize", icon: Zap },
              { task: "Use vector software (Illustrator, Figma, Sketch)", icon: Zap },
              { task: "Refine shapes, typography, and spacing", icon: Zap },
              { task: "Create versions in black and white", icon: Zap },
            ].map((item, index) => (
              <motion.div
                key={item.task}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-4 rounded-lg border border-gray-800 flex gap-4"
              >
                <item.icon className="w-8 h-8 text-coral flex-shrink-0" />
                <p className="text-muted-foreground">{item.task}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Step 4: Color Selection */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Step 4: Color Selection
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground mb-6"
          >
            Choose colors that align with your brand personality and industry. Color psychology plays a crucial role in how your logo is perceived.
          </motion.p>
          <div className="space-y-4">
            {[
              { task: "Research color psychology for your industry", icon: Layers },
              { task: "Limit to 1-2 primary colors for impact", icon: Layers },
              { task: "Test color combinations for contrast and accessibility", icon: Layers },
              { task: "Ensure colors work in both digital and print", icon: Layers },
            ].map((item, index) => (
              <motion.div
                key={item.task}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-4 rounded-lg border border-gray-800 flex gap-4"
              >
                <item.icon className="w-8 h-8 text-coral flex-shrink-0" />
                <p className="text-muted-foreground">{item.task}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Step 5: Testing */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Step 5: Testing and Refinement
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground mb-6"
          >
            Test your logo across various applications and gather feedback. This ensures your design works in real-world scenarios and resonates with your audience.
          </motion.p>
          <div className="space-y-4">
            {[
              { task: "Test at various sizes (favicon to billboard)", icon: Eye },
              { task: "Verify readability in black and white", icon: Eye },
              { task: "Check on different backgrounds and contexts", icon: Eye },
              { task: "Gather feedback from target audience", icon: Eye },
            ].map((item, index) => (
              <motion.div
                key={item.task}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-4 rounded-lg border border-gray-800 flex gap-4"
              >
                <item.icon className="w-8 h-8 text-coral flex-shrink-0" />
                <p className="text-muted-foreground">{item.task}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Step 6: Final Delivery */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Step 6: Final Delivery
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground mb-6"
          >
            Prepare your logo for all applications by creating multiple file formats and usage guidelines. This ensures consistent brand implementation.
          </motion.p>
          <div className="space-y-4">
            {[
              { task: "Export in vector formats (AI, EPS, SVG)", icon: Shield },
              { task: "Create raster versions (PNG, JPG at various sizes)", icon: Shield },
              { task: "Provide brand guidelines document", icon: Shield },
              { task: "Include color codes and typography specs", icon: Shield },
            ].map((item, index) => (
              <motion.div
                key={item.task}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-4 rounded-lg border border-gray-800 flex gap-4"
              >
                <item.icon className="w-8 h-8 text-coral flex-shrink-0" />
                <p className="text-muted-foreground">{item.task}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Common Logo Design Mistakes to Avoid
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { mistake: "Skipping Research", fix: "Always research before designing." },
              { mistake: "Overcomplicating", fix: "Keep it simple and focused." },
              { mistake: "Ignoring Scalability", fix: "Test at all sizes." },
              { mistake: "Following Trends", fix: "Focus on timeless design." },
            ].map((item, index) => (
              <motion.div
                key={item.mistake}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-lg mb-2 text-red-400">{item.mistake}</h3>
                <p className="text-muted-foreground">{item.fix}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DIY vs Professional */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            DIY vs Professional Logo Design
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              className="bg-card p-6 rounded-lg border border-gray-800"
            >
              <h3 className="font-semibold text-xl mb-4 text-coral">Professional Design</h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Strategic approach</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Professional expertise</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> All file formats included</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Brand guidelines provided</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Revisions and refinements</li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              
              className="bg-card p-6 rounded-lg border border-gray-800"
            >
              <h3 className="font-semibold text-xl mb-4 text-muted-foreground">DIY Design</h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-muted-foreground" /> Lower cost upfront</li>
                <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-muted-foreground" /> Creative control</li>
                <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-muted-foreground" /> Learning opportunity</li>
                <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-muted-foreground" /> Quick iterations</li>
                <li className="flex items-center gap-2"><Zap className="w-4 h-4 text-muted-foreground" /> No dependency on others</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Content */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Related Resources
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Ultimate Guide to Logo Design Services", url: "/logo-design-services-guide", desc: "Comprehensive guide to professional logo design" },
              { title: "What Makes a Good Logo", url: "/what-makes-a-good-logo", desc: "Learn the essential qualities of effective logos" },
              { title: "Logo Design Principles", url: "/logo-design-principles", desc: "Master the fundamental design principles" },
              { title: "Logo Design Tools Comparison", url: "/logo-design-tools-comparison", desc: "Compare the best设计 software" },
            ].map((resource, index) => (
              <motion.a
                key={resource.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                href={resource.url}
                className="bg-card p-6 rounded-lg border border-gray-800 hover:border-coral transition-colors block"
              >
                <h3 className="font-semibold text-lg mb-2">{resource.title}</h3>
                <p className="text-muted-foreground">{resource.desc}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-4"
          >
            Need Professional Logo Design?
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-xl text-muted-foreground mb-8"
            >
            Our expert designers follow this proven process to create logos that build brand recognition and drive business success.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Start Your Logo Project
          </motion.button>
        </div>
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </main>
  );
};

export default HowToDesignLogo;
